import React, { useEffect, useRef, useState, useSyncExternalStore } from "react";
import PropTypes from "prop-types";
import { ArrowLeft, Calendar, Heart, Menu, Moon, Sun, X } from "lucide-react";
import Button from "./ui/Button";
import BrandMark from "./BrandMark";
import SiteBrowseLinks from "./SiteBrowseLinks";
import useWatchlist from "../hooks/useWatchlist";
import { cn } from "../utils/classNames";
import { useTheme } from "../contexts/ThemeContext";
import { getPreviousPageTitle, handleInternalLinkClick, resolveBackLink } from "../utils/navigation";

const subscribeToHistory = (onChange) => {
  window.addEventListener("popstate", onChange);
  return () => window.removeEventListener("popstate", onChange);
};

const SubpageHeader = React.forwardRef(({
  className = "",
  backHref = "/",
  backLabel = "Home",
  actionContent = null,
  showMyListAction = true,
  ...props
}, ref) => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const { watchlistCount } = useWatchlist();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  // History state is only readable in the browser; the server snapshot (null) keeps
  // the prerendered link to the parent page, which hydration then updates.
  const previousTitle = useSyncExternalStore(subscribeToHistory, getPreviousPageTitle, () => null);
  const back = resolveBackLink({ parentHref: backHref, parentLabel: backLabel, previousTitle });

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  return (
    <header
      ref={ref}
      className={cn(
        "sticky top-0 z-50 border-b border-accent-gold/35 bg-background shadow-header",
        className
      )}
      {...props}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-10 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-body-small focus:font-semibold focus:text-primary-foreground"
      >
        Skip to content
      </a>
      {/* Film strip top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-film-strip" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="subpage-header__inner flex h-16 sm:h-20 min-h-[4rem] sm:min-h-[5rem] items-center gap-4">
          {/* Brand */}
          <BrandMark href="/" />

          {/* Actions */}
          <div className="relative ml-auto flex items-center gap-2">
            <nav className="hidden xl:flex items-center gap-2" aria-label="Browse TV"><SiteBrowseLinks /></nav>
            <Button asChild variant="ghost" className="subpage-header__action subpage-header__action--back">
              <a
                href={back.href}
                aria-label={back.name}
                onClick={(event) => {
                  if (!back.inApp) {
                    handleInternalLinkClick(event, back.href);
                    return;
                  }
                  if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
                  event.preventDefault();
                  window.history.back();
                }}
              >
                <div className="subpage-header__action-icon">
                  <ArrowLeft className="h-5 w-5" />
                </div>
                <span className="subpage-header__action-label">{back.text}</span>
              </a>
            </Button>
            {showMyListAction && (
              <Button asChild variant="ghost" className="subpage-header__action hidden sm:flex">
                <a
                  href="/my-list"
                  onClick={(event) => handleInternalLinkClick(event, "/my-list")}
                  aria-label={`Open My List${watchlistCount ? `, ${watchlistCount} saved shows` : ""}`}
                >
                  <div className="subpage-header__action-icon">
                    <Heart className="h-5 w-5" />
                    {watchlistCount > 0 && (
                      <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center border border-background bg-secondary px-1 py-0.5 text-micro font-bold leading-none text-secondary-foreground">
                        {watchlistCount > 99 ? "99+" : watchlistCount}
                      </span>
                    )}
                  </div>
                  <span className="subpage-header__action-label">My List</span>
                </a>
              </Button>
            )}
            <Button asChild variant="ghost" className="subpage-header__action hidden xl:flex">
              <a href="/archive" onClick={(event) => handleInternalLinkClick(event, "/archive")}>
                <div className="subpage-header__action-icon">
                  <Calendar className="h-5 w-5" />
                </div>
                <span className="subpage-header__action-label">Archive</span>
              </a>
            </Button>
            {actionContent}
            <Button
              ref={menuButtonRef}
              type="button"
              variant="ghost"
              onClick={() => setMenuOpen((current) => !current)}
              aria-expanded={menuOpen}
              aria-controls="compact-subpage-navigation"
              aria-label="Menu"
              className="subpage-header__action flex xl:hidden"
            >
              <div className="subpage-header__action-icon">
                {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
              </div>
              <span className="subpage-header__action-label subpage-header__action-label--short">Menu</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
              className="subpage-header__theme"
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>
            {menuOpen && (
              <nav
                id="compact-subpage-navigation"
                aria-label="Page navigation"
                className="absolute right-0 top-[calc(100%+0.75rem)] z-50 w-56 rounded-lg border border-accent-gold/60 bg-card p-2 shadow-lg xl:hidden"
              >
                <SiteBrowseLinks onNavigate={() => setMenuOpen(false)} />
                <a className="site-browse-link" href="/archive" onClick={(event) => { setMenuOpen(false); handleInternalLinkClick(event, "/archive"); }}>Archive</a>
                {showMyListAction && (
                  <a
                    href="/my-list"
                    className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-left text-body-small font-semibold text-foreground hover:bg-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={(event) => { setMenuOpen(false); handleInternalLinkClick(event, "/my-list"); }}
                  >
                    <Heart className="h-5 w-5 text-primary" aria-hidden="true" />
                    My List
                    {watchlistCount > 0 && <span className="ml-auto text-meta">{watchlistCount > 99 ? "99+" : watchlistCount}</span>}
                  </a>
                )}
              </nav>
            )}
          </div>
        </div>
      </div>
    </header>
  );
});

SubpageHeader.displayName = "SubpageHeader";

SubpageHeader.propTypes = {
  className: PropTypes.string,
  backHref: PropTypes.string,
  backLabel: PropTypes.string,
  actionContent: PropTypes.node,
  showMyListAction: PropTypes.bool,
};

export default SubpageHeader;
